/**
 * This class is ONLY used for QUEUES!
 */

import type {IndexableType, Table} from 'dexie';
import Dexie, {liveQuery} from 'dexie';
import ls from './localStorage';
import {EntityType} from "@/@types/entity";
import {getSessionFirmId, getSessionUserId} from "./picos/sessionIdentity";
import {Err, None, Ok, Option, Result, Some} from "oxide.ts";
import {cast} from "@/entities/schemas";
import {queueCountSlice} from "@/store/queueCount";
import {persistenceGate} from '../Auth/persistenceGate';
import {tabSession} from '../Auth/session';
import {isQueuedFileBytes, isRenameOnlyUpload, type QueuedFileBytes} from '../Documents/documentUploadRouting';
import type Document from '../entities/document';
import newHRTimestamp from './picos/timestamp';
import type {StaleConflict, VersionRef} from './staleSave';

let logging = ls.getBoolean(ls.QUEUE_LOGGING, false);

// This is the IDB that we will be working with: Queues
let _db: Option<Dexie> = None;

/** The database name `_db` was created for, so we can tell when identity moves. */
let _dbName = '';

/** Handles whose version chain has already been declared. */
const configuredDatabases = new WeakSet<Dexie>();

let _isOldVersion = false;

/**
 * The name of the queue database for an identity.
 *
 * The pre-version-35 unscoped `Queues` database is accessed only through the
 * explicit migration helpers below; active queues always have an owner.
 *
 * @param {string} firmId
 * @param {string} userId
 * @returns {string}
 */
function queueDbName(firmId: string, userId: string): string {
    return `Queues_${firmId}_${userId}`;
}

/** Message for an unknown thrown value (handles non-Error throws). */
function errMsg(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

// Durable work may finish for its captured database after the tab locks or
// changes account. Only publish its count into the same tab generation/owner.
function captureCountPublisher(): (update: (count: number) => number) => void {
    const generation = tabSession.captureGeneration();
    const firmId = getSessionFirmId();
    const userId = getSessionUserId();
    return update => {
        if (generation.signal.aborted || firmId !== getSessionFirmId() || userId !== getSessionUserId()) return;
        queueCountSlice.set('count', update(queueCountSlice.get('count')));
    };
}

/**
 *
 * @param {string} [firmId]
 * @param {string} [userId]
 * @returns {Option<Dexie>}
 */
export function getDB(firmId?: string, userId?: string): Result<Dexie, Error> {
    const ownerFirm = firmId ?? getSessionFirmId();
    const ownerUser = userId ?? getSessionUserId();
    if (!ownerFirm || !ownerUser) return Err(new Error('A complete tab identity is required to open its queue.'));
    const dbName = queueDbName(ownerFirm, ownerUser);

    if (_db.isSome()) {
        // The upgrade path (getOldDb) deliberately holds the unscoped
        // pre-version-35 database; leave it alone until close() releases it.
        if (_isOldVersion) return Ok(_db.unwrap());

        // Serving a cached instance regardless of the identity asked for is how
        // a pre-auth `Queues` handle, or the first tab's firm, stuck for the
        // life of the page (OWA-785). Only reuse the handle when it is the one
        // the caller actually asked for.
        if (_dbName === dbName) return Ok(_db.unwrap());

        // A caller with no identity at all (mid-logout, say) must not tear down
        // a live queue database holding unflushed work.

        // Replace the cached pointer, but do NOT close the instance being
        // replaced. A caller can be part-way through an operation holding that
        // handle, and closing it underneath them fails the operation. Letting
        // the old connection go unreferenced costs one lingering IDB handle
        // until the page unloads, which is the cheaper of the two mistakes.
        if (logging) console.debug(`⟴ IDB: getDB() identity moved from "${_dbName}" to "${dbName}", replacing the instance`);
        _db = None;
        _dbName = '';
    }

    try {
        if (logging) console.debug(`⟴ IDB: getDB() creating new Dexie instance: "${dbName}"`);
        const db = new Dexie(dbName);
        _db = Some(db);
        _dbName = dbName;
        return Ok(db);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: getDB() failed to create Dexie instance "${dbName}". ${errMsg(err)}`));
    }
}

export function getOldDb(): Option<Dexie> {
    try {
        const db = new Dexie(`Queues`);
        _isOldVersion = true;
        _db = Some(db);
        _dbName = 'Queues';
        return _db;
    }
    catch (err) {
        return None;
    }
}

const INITIAL_TABLES = ['user', 'firm', 'goal', 'task', 'contact'];
let tables: string[] = [];

export const VERSION = 40;

/**
 * Create a new version schema for the database.
 *
 * CAUTION: In order to make this function usable, we must assume that the database is available.
 * Thus calls to the method MUST CHECK that the database is open FIRST.  We will log an error if
 * it is not the case.
 *
 * The instance is passed in rather than re-fetched. `open(firmId, userId)` may
 * be scoping a database that is NOT the one this tab's session names, and
 * calling getDB() again here would resolve the session's identity instead,
 * declaring the schema on a different database from the one open() returns.
 *
 * @param {number} version
 * @param {Dexie} db - the instance open() resolved, already scoped to the target identity
 */
function createVersion(version: number, db: Dexie): number {
    const stores: Record<string, string> = {};

    tables.forEach(e => {
        if (_isOldVersion) stores[e] = `_addedOn, ${e}Id`;
        else stores[e] = `addedOn, ${e}Id`;
    });

    db.version(++version).stores(stores);

    return version;
}

/**
 * Check if the database is open.
 */
export function isOpen(): Result<boolean, Error> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: isOpen() failed — no database instance. ${dbResult.unwrapErr().message}`));

    return Ok(dbResult.unwrap().isOpen());
}

/**
 * Close the database and sets the internal db to None.
 */
export function close(): Result<void, Error> {
    // Act on the handle we are actually holding. Going through getDB() would
    // mint a fresh instance for the current identity and close that instead,
    // leaving the real one open (OWA-785 made getDB() identity-sensitive).
    if (_db.isSome()) {
        const db = _db.unwrap();
        if (logging) console.debug(`⟴ IDB: close() — db="${db.name}", wasOpen=${db.isOpen()}`);
        if (db.isOpen()) db.close();
        _db = None;
        _dbName = '';
        // A new session's saves never follow a closed session's restamped rows
        restampedQueues.clear();
        _isOldVersion = false;
        return Ok(null);
    }
    return Err(new Error(`⟴ IDB: close() failed — no database instance.`));
}

/**
 * Open the database.
 *
 * This function contains the schema for all versions of the database (can not automatically generate)
 *
 * The explicit pair scopes BOTH the database selected and the schema declared
 * on it, and the returned handle is the one the schema was declared on. It does
 * not bind later calls: every queue operation resolves this tab's own identity,
 * which is the right default for a per-tab queue. Pass the pair when you mean a
 * specific database, and use the returned handle for that work.
 *
 * @param {string} [firmId] - if passed, forces the firm half of the database name
 * @param {string} [userId] - if passed, forces the user half of the database name
 * @returns {Dexie}
 */
export function open(firmId?: string, userId?: string): Result<Dexie, Error> {
    const dbResult = getDB(firmId, userId);
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: open(firmId=${firmId ?? 'undefined'}, userId=${userId ?? 'undefined'}) failed — could not get database instance. ${dbResult.unwrapErr().message}`));
    const db = dbResult.unwrap();
    // Authentication opens the queue before restore runs. Migration and other
    // callers may ask for the same handle again while it is opening or after it
    // is open; Dexie forbids redeclaring versions in either state.
    if (configuredDatabases.has(db)) return Ok(db);

    // Reset tables to initial state so open() is safe to call multiple times
    tables = [...INITIAL_TABLES];

    // First version will be ++version, so 1
    let version = 0;

    // Uses initial tables
    version = createVersion(version, db); // v1

    // For some reason the next three are the same...
    tables = tables.concat(['address', 'category', 'comment', 'company', 'document', 'employment', 'event', 'provider', 'relation', 'resource']);
    version = createVersion(version, db); // v2
    version = createVersion(version, db); // v3
    version = createVersion(version, db); // v4

    tables = tables.concat(['referral']);
    version = createVersion(version, db); // v5

    tables = tables.concat(['campaign', 'campaignResponse', 'lead', 'opportunity', 'opportunityAction']);
    version = createVersion(version, db); // v6

    tables = tables.concat(['relationType']);
    version = createVersion(version, db); // v7

    tables = tables.concat(['identification']);
    version = createVersion(version, db); // v8

    // Remove campaignResponse, add others
    const index = tables.indexOf('campaignResponse');
    tables.splice(index, 1);
    tables = tables.concat(['caseType', 'conflict', 'milestone', 'question', 'answer', 'redFlagType', 'redFlag']);
    version = createVersion(version, db); // v9

    tables = tables.concat(['option']);
    version = createVersion(version, db); // v10

    tables = tables.concat(['activity']);
    version = createVersion(version, db); // v11

    tables = tables.concat(['activityCategory', 'activityType', 'matter', 'expense', 'transaction']);
    version = createVersion(version, db); // v12

    tables = tables.concat(['timer']);
    version = createVersion(version, db); // v13

    tables = tables.concat(['role', 'privilege', 'group']);
    version = createVersion(version, db); // v14

    tables = tables.concat(['appointmentEntry']);
    version = createVersion(version, db); // v15

    tables = tables.concat(['lookup']);
    version = createVersion(version, db); // v16

    tables = tables.concat(['bankAccount']);
    version = createVersion(version, db); // v17

    tables = tables.concat(['invoice', 'discount']);
    version = createVersion(version, db); // v18

    tables = tables.concat(['participant']);
    version = createVersion(version, db); // v19

    tables = tables.concat(['importExport']);
    version = createVersion(version, db); // v20

    tables = tables.concat(['importSource']);
    version = createVersion(version, db); // v21

    tables = tables.concat(['escrow']);
    version = createVersion(version, db); // v22

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV23 = ['action', 'opportunity', 'opportunityAction', 'timer'];
    toRemoveV23.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });
    version = createVersion(version, db); // v23

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV24 = ['relation', 'relationType'];
    toRemoveV24.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });
    version = createVersion(version, db); // v24

    tables = tables.concat(['flatFee']);
    version = createVersion(version, db); // v25

    tables = tables.concat(['flatFeeMilestone']);
    version = createVersion(version, db); // v26

    tables = tables.concat(['like']);
    version = createVersion(version, db); // v27

    tables = tables.concat(['templateTask']);
    version = createVersion(version, db); // v28

    tables = tables.concat(['blackout', 'docket', 'timeOff']);
    version = createVersion(version, db); // v29

    tables = tables.concat(['paymentMethod']);
    version = createVersion(version, db); // v30

    tables = tables.concat(['invoiceBatch', 'invoiceBatchItem']);
    version = createVersion(version, db); // v31

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV32 = [
        'appointmentEntry', 'company', 'employment', 'escrow', 'goal',
        'PaymentMethod', 'provider', 'testChild', 'testParent'
    ]; // v32

    toRemoveV32.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });

    tables = tables.concat(['entityTimestamp']);
    version = createVersion(version, db); // v33

    // This version has no new tables, but we need to change the keys and values for anything already in the cahce,
    // as we no longer use underscores in variable names.
    version = createVersion(version, db); // v35 CHANGE THE CONSTANT WHEN YOU CHANGE THIS!

    // Drop the 'like' tables
    tables = tables.filter(table => table !== 'like');
    version = createVersion(version, db);

    // Add the new accounting entities
    tables = tables.concat(['accountingConnection']);
    version = createVersion(version, db);

    // v32 splice bugs accidentally removed docket, invoiceBatch, invoiceBatchItem, and timeOff
    // (caused by indexOf returning -1 for missing entries, making splice(-1,1) remove the last element).
    // Re-add those, plus entities that were never added to IDB.
    tables = tables.concat([
        'caseTypeStage', 'deadline', 'deadlineRule', 'docket',
        'invoiceBatch', 'invoiceBatchItem', 'matterStage', 'timeOff',
        'court', 'courtDeadlineRule', 'holidayCalendar'
    ]);
    version = createVersion(version, db);

    // Recurring Tasks (OWA-1314) added taskSeries without a queue table. This schema is declared at
    // 39: the steps above declared 36 while VERSION read 38, so the counter is moved up to 39. A
    // database at 36 upgrades straight to 39. A new schema step goes at the end, and raises VERSION
    // by one.
    tables = tables.concat(['taskSeries']);
    version = createVersion(38, db); // v39

    // Calendars (OWA-1313) added calendar and calendarShare without queue tables (v40).
    // idbd.version.test.ts fails when a saga writes to a store that is not declared here.
    tables = tables.concat(['calendar', 'calendarShare']);
    version = createVersion(version, db);
    if (version !== VERSION) throw new Error(`⟴ IDB: ${version} schema steps but VERSION is ${VERSION}; raise VERSION`);

    ls.setNumeric(ls.IDB_VERSION_CURRENT, version);

    // Reuse the instance captured above — defining schema versions never loses it.
    ls.setNumeric(ls.IDB_VERSION_RUNNING, db.verno);
    configuredDatabases.add(db);

    if (logging) console.debug(`⟴ IDB: open() completed — db="${db.name}", version=${db.verno}, tables=${db.tables.length} [${db.tables.map(t => t.name).join(', ')}]`);

    return Ok(db);
}

/**
 * Declare the queue schema and wait for the browser to open it.
 *
 * `open()` remains synchronous for callers that only need a configured Dexie
 * handle. Authentication uses this stronger boundary so a workspace is not
 * announced as ready until IndexedDB has actually opened and upgraded.
 */
export async function openReady(firmId: string, userId: string): Promise<Result<Dexie, Error>> {
    const configured = open(firmId, userId);
    if (configured.isErr()) return configured;
    const db = configured.unwrap();
    if (db.isOpen()) return configured;

    try {
        await db.open();
        return Ok(db);
    }
    catch (err) {
        // A failed lazy open must not poison the cached handle. A later retry
        // should build a fresh Dexie instance for the same account.
        if (_db.isSome() && _db.unwrap() === db) {
            db.close();
            _db = None;
            _dbName = '';
        }
        return Err(new Error(`⟴ IDB: openReady(firmId=${firmId}, userId=${userId}) failed. ${errMsg(err)}`));
    }
}

/**
 * Clear all tables (stores).  CAREFUL!
 * @returns {Promise<void>}
 */
export async function clearErrors(): Promise<Result<void, Error>> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: clearErrors() failed — no database instance. ${dbResult.unwrapErr().message}`));

    const db = dbResult.unwrap();
    console.warn(`⟴ IDB: clearErrors() clearing all tables in db="${db.name}" (${db.tables.length} tables)`);

    try {
        await db.transaction('rw', db.tables, async () => {
            await Promise.all(db.tables.map(table => table.clear()));
        });
        publishCount(() => 0);
        return Ok(null);
    } catch (error) {
        return Err(new Error(`The queued changes could not be cleared. ${errMsg(error)}`));
    }
}

/**
 * Get a list of all database stores.
 */
export async function getStoreNames(): Promise<IDBDatabaseInfo[]> {
    return await indexedDB.databases();
}

/**
 * Row counts per object store of the raw pre-version-35 'Queues' database
 * (OWA-786, CU-5-C).
 *
 * Opens the database in Dexie's dynamic mode (no declared versions), so the
 * inventory reflects the object stores that ACTUALLY exist on disk — including
 * stores dropped from the current version chain, which the schema-driven
 * migration read cannot see. The queue migration uses this to prove the copy
 * before deleting the old database.
 *
 * Ok(null) means the database does not exist (nothing to migrate — dynamic
 * mode never creates one). Err means it exists but could not be read; the
 * caller must treat that as "cannot prove, do not delete".
 */
export async function oldQueueInventory(): Promise<Result<Map<string, number> | null, Error>> {
    const db = new Dexie('Queues');
    try {
        await db.open();
        const counts = new Map<string, number>();
        for (const table of db.tables) {
            counts.set(table.name, await table.count());
        }
        return Ok(counts);
    }
    catch (err) {
        if ((err as any)?.name === 'NoSuchDatabaseError') return Ok(null);
        return Err(new Error(`⟴ IDB: oldQueueInventory() failed. ${errMsg(err)}`));
    }
    finally {
        if (db.isOpen()) db.close();
    }
}

/** Read an old queue without replacing this tab's active account handle.
 * Check RAW ownership first: entity constructors fill missing owner fields from
 * the current session, which is not evidence that an unscoped row belongs to it.
 */
export async function readOwnedOldQueueRows(firmId: string, userId: string, supportedStores: string[]): Promise<Result<StoreResult, Error>> {
    const db = new Dexie('Queues');
    try {
        await db.open();
        const result: StoreResult = {};
        for (const table of db.tables) {
            if (!supportedStores.includes(table.name)) continue;
            const rows = await table.toArray();
            const owned = rows.filter(row => {
                const firms = [row._firmId, row.firmId, row.firmid].filter(value => value != null && value !== '');
                const users = [row._addedBy, row.addedBy, row.addedby].filter(value => value != null && value !== '');
                return firms.length > 0 && users.length > 0 && firms.every(value => value === firmId) && users.every(value => value === userId);
            });
            if (owned.length) result[table.name] = owned.map(row => cast<EntityType>(table.name, row));
        }
        return Ok(result);
    } catch (error) {
        if ((error as any)?.name === 'NoSuchDatabaseError') return Ok({});
        return Err(new Error(`The old queued changes could not be read. ${errMsg(error)}`));
    } finally {
        if (db.isOpen()) db.close();
    }
}

/**
 * Retire proven-copied rows from one store of the raw pre-version-35 'Queues'
 * database (OWA-786, CXR-9-002/CXR-10-002).
 *
 * The queue migration retires ONLY the rows it has proven copied, through
 * ordinary row transactions — never `indexedDB.deleteDatabase`, whose request
 * pends unbounded while another tab holds the database open and then fires
 * later, destroying rows written after the proof.
 *
 * Retirement is bound to the proved row IMAGE, and row identity comes from
 * the store's own cursor — never from the image (CXR-11-001): the cast that
 * produced the image normalizes `_addedOn` to a number, while the raw primary
 * key can be the original string, and IndexedDB distinguishes the two. Inside
 * one read-write transaction the store's rows and primary keys are read in
 * cursor order, each raw row is cast (the same deterministic transform that
 * produced the proved image, so an unchanged row compares equal), and a row
 * is deleted BY ITS CURSOR KEY only when its image matches a proved image. A
 * row another tab rewrote at the same key after the proof therefore survives
 * — it is that tab's only copy of the rewrite — and migrates on a later
 * startup, as does any row enqueued at a new key. Two byte-identical rows at
 * different keys matching one proved image are both retired: identical
 * durable intent.
 *
 * Ok(0) when the database or the store does not exist (nothing to retire);
 * otherwise Ok(count of rows actually retired). Err when the store exists
 * but could not be read or written; the caller treats that as fail-soft (the
 * proven rows re-copy idempotently on a later startup).
 */
export async function deleteOldQueueRows(store: string, provedRows: EntityType[]): Promise<Result<number, Error>> {
    const db = new Dexie('Queues');
    try {
        await db.open();
        const table = db.tables.find(t => t.name === store);
        if (!table) return Ok(0);
        const provedImages = new Set(provedRows.map(proved => JSON.stringify(proved)));
        let deleted = 0;
        await db.transaction('rw', store, async () => {
            // toArray() and primaryKeys() both iterate in primary-key order
            // within this one transaction, so values and keys align.
            const raws = await table.toArray();
            const keys = await table.toCollection().primaryKeys();
            for (let i = 0; i < raws.length; i++) {
                if (!provedImages.has(JSON.stringify(cast(store, raws[i])))) {
                    if (logging) console.debug(`⟴ IDB: deleteOldQueueRows("${store}") row at key ${String(keys[i])} does not match a proved image — leaving it for a later migration`);
                    continue;
                }
                await table.delete(keys[i]);
                deleted++;
            }
        });
        return Ok(deleted);
    }
    catch (err) {
        if ((err as any)?.name === 'NoSuchDatabaseError') return Ok(0);
        return Err(new Error(`⟴ IDB: deleteOldQueueRows("${store}", ${provedRows.length} row(s)) failed. ${errMsg(err)}`));
    }
    finally {
        if (db.isOpen()) db.close();
    }
}

export type StoreResult = Record<string, EntityType[]>;

function isDataCloneError(error: unknown): boolean {
    const cause = error as {name?: string; message?: string; inner?: {name?: string; message?: string}} | null;
    return cause?.name === 'DataCloneError' || cause?.inner?.name === 'DataCloneError' ||
        ((cause?.name === 'UnknownError' || cause?.inner?.name === 'UnknownError') &&
            (cause?.message?.includes('Error preparing Blob/File data to be stored in object store') ||
                cause?.inner?.message?.includes('Error preparing Blob/File data to be stored in object store')));
}

async function prepareDocumentFile(item: EntityType): Promise<EntityType> {
    const file = (item as EntityType & {fileHandle?: unknown}).fileHandle;
    if (!(file instanceof Blob)) return item;
    const document = item as EntityType & {fileName?: string; contentType?: string};
    const queuedFile: QueuedFileBytes = {
        queuedFileBytes: true,
        bytes: await file.arrayBuffer(),
        name: file instanceof File ? file.name : (document.fileName || 'document'),
        type: file.type || document.contentType || '',
        lastModified: file instanceof File ? file.lastModified : 0,
    };
    return {...item, fileHandle: queuedFile} as EntityType;
}

function restoreDocumentFile(store: string, item: EntityType): EntityType {
    if (store !== 'document') return item;
    const queuedFile = (item as EntityType & {fileHandle?: unknown}).fileHandle;
    if (queuedFile instanceof Blob && !(queuedFile instanceof File)) {
        const document = item as EntityType & {fileName?: string};
        return {...item, fileHandle: new File([queuedFile], document.fileName || 'document', {type: queuedFile.type})} as EntityType;
    }
    if (!isQueuedFileBytes(queuedFile)) return item;
    return {...item, fileHandle: new File([queuedFile.bytes], queuedFile.name, {
        type: queuedFile.type,
        lastModified: queuedFile.lastModified,
    })} as EntityType;
}

function getTable(store: string, capturedDB?: Dexie): Result<Table<any, any, any>, Error> {
    // Every caller verifies getDB() first, so the instance is cached here.
    const db = capturedDB ?? getDB().unwrap();
    const table = db.tables.find(t => t.name === store);
    if (!table) {
        const available = db.tables.length > 0 ? db.tables.map(t => t.name).join(', ') : '(none — idb.open() was never called or db was closed)';
        if (logging) console.debug(`⟴ IDB: getTable("${store}") — store not found. db="${db.name}", isOpen=${db.isOpen()}, tables=${db.tables.length} [${available}]`);
        return Err(new Error(`⟴ IDB: getTable("${store}") failed — store not found in db "${db.name}" (${db.tables.length} tables). Available: [${available}]`));
    }
    return Ok(table);
}

/**
 * Get all items from a single store.
 *
 * @param {string} storeName
 * @returns {Promise<any[]>}
 * @async
 */
export async function getSingleStore(storeName: string, capturedDB?: Dexie): Promise<Result<EntityType[], Error>> {
    const dbResult = capturedDB ? Ok(capturedDB) : getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: getSingleStore("${storeName}") failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(storeName, dbResult.unwrap());
    if (table.isErr()) {
        return Err(table.unwrapErr());
    }

    try {
        const result = await table.unwrap().toArray();
        const casts = result.map(item => {
            const entity = cast<EntityType>(storeName, restoreDocumentFile(storeName, item));
            // Constructors omit queue-only metadata. Restore it for queue
            // readers, but keep it off wire JSON sent to the server.
            // OWA-998: a task row's lineage (opId, parentOp), why it was saved
            // (saveIntent) and, when refused as stale, the conflict that holds it.
            for (const key of ['retryAttempts', 'nextRetryAt', 'supersededByAddedOn', 'opId', 'parentOp', 'saveIntent', 'conflict', 'resolves'] as const) {
                if (item[key] != null) Object.defineProperty(entity, key, {
                    value: item[key], writable: true, configurable: true,
                });
            }
            return entity;
        });
        return Ok(casts);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: getSingleStore("${storeName}") failed during toArray(). ${errMsg(err)}`));
    }
}

export async function blockingDocumentUpload(candidate: Document): Promise<Result<'failed' | 'pending' | null, Error>> {
    const db = getDB();
    if (db.isErr()) return Err(db.unwrapErr());
    const table = getTable('document', db.unwrap());
    if (table.isErr()) return Err(table.unwrapErr());

    try {
        const unsuperseded = await table.unwrap().where('documentId').equals(candidate.documentId)
            .filter(row => !row.supersededByAddedOn).toArray();
        if (unsuperseded.some(row => !!(row.fatalError?.message || row.fatalError?.status))) return Ok('failed');
        return Ok(unsuperseded.some(row => !acknowledgedReplacedDocumentContents(row, candidate)) ? 'pending' : null);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: blockingDocumentUpload failed. ${errMsg(err)}`));
    }
}

/** Keep rejected document versions visible even after a newer cascade replaces Redux's one-row view. */
export function observeFailedDocumentUploads(onChange: (rows: Document[]) => void): Result<() => void, Error> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(dbResult.unwrapErr());
    const tableResult = getTable('document', dbResult.unwrap());
    if (tableResult.isErr()) return Err(tableResult.unwrapErr());
    const firmId = getSessionFirmId();
    const userId = getSessionUserId();
    const table = tableResult.unwrap();
    const subscription = liveQuery(async () => {
        const rows = await table.filter(row => !row.supersededByAddedOn &&
            !!(row.fatalError?.message || row.fatalError?.status)).toArray();
        return rows.map(row => cast<Document>('document', restoreDocumentFile('document', row)));
    }).subscribe({
        next: rows => {
            if (firmId !== getSessionFirmId() || userId !== getSessionUserId()) return;
            onChange(rows);
        },
        error: error => console.error('Could not read rejected document uploads', error),
    });
    return Ok(() => subscription.unsubscribe());
}

/**
 * OWA-998: every queued task save, with its queue-only metadata, whenever the task queue changes.
 * The held-save notices (dialog, Needs Attention, editor banner) group these.
 */
export function observeTaskQueue(onChange: (rows: EntityType[]) => void): Result<() => void, Error> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(dbResult.unwrapErr());
    const db = dbResult.unwrap();
    const tableResult = getTable('task', db);
    if (tableResult.isErr()) return Err(tableResult.unwrapErr());
    const firmId = getSessionFirmId();
    const userId = getSessionUserId();
    const subscription = liveQuery(async () => (await getSingleStore('task', db)).unwrap()).subscribe({
        next: rows => {
            if (firmId !== getSessionFirmId() || userId !== getSessionUserId()) return;
            onChange(rows);
        },
        error: error => console.error('Could not read queued task saves', error),
    });
    return Ok(() => subscription.unsubscribe());
}

/**
 * This is called from Help, so we take errors and put them in the resulting object instead of throwing them.
 *
 * @async
 * @returns {Promise<Result<any[], Error>>}
 */
export async function getAll(): Promise<Result<StoreResult, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: getAll() failed — no database instance. ${dbResult.unwrapErr().message}`));

    // Get all stores
    const db = dbResult.unwrap();
    const result: StoreResult = {};

    for (let i = 0; i < db.tables.length; i++) {
        const name = db.tables[i].name;
        const storeResult = await getSingleStore(name, db);
        if (storeResult.isOk()) {
            const items = storeResult.unwrap();
            if (items.length > 0) result[name] = items;
        }
        else {
            console.warn(`⟴ IDB: getAll() skipping store "${name}" (${i + 1}/${db.tables.length}). ${storeResult.unwrapErr().message}`);
        }
    }

    return Ok(result);
}

/**
 * Reset the fatalError for all entries in the given store, or all stores if no store is given.
 *
 * @param {string} store
 */
export async function resetAll(store?: string): Promise<Result<boolean, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(dbResult.unwrapErr());
    const db = dbResult.unwrap();
    // Keep reads and writes on the same account handle across asynchronous reads.
    let items: StoreResult = {};

    if (store) {
        const values = await getSingleStore(store, db);
        if (values.isErr()) return Err(new Error(`⟴ IDB: resetAll("${store}") failed to read store. ${values.unwrapErr().message}`));
        else items[store] = values.unwrap();
    }
    else {
        const _items = await getAll();
        if (_items.isErr()) return Err(new Error(`⟴ IDB: resetAll() failed to read all stores. ${_items.unwrapErr().message}`));
        else items = _items.unwrap();
    }

    for (const storeName of Object.keys(items)) {
        // storeName came from getSingleStore()/getAll(), so the table exists.
        const t = db.table(storeName);
        for (const item of items[storeName]) {
            // Retained document versions are recovery copies, not operations
            // waiting for another attempt.
            if (storeName === 'document' && item.supersededByAddedOn) continue;
            // Update just the error; replacing a cast entity can drop the
            // File, note, retry state, and other original queue-row fields.
            await t.update(item.addedOn, {fatalError: null});
        }
    }

    return Ok(true);
}

/**
 * Retrieve an item from a specific store by its index.
 *
 * @param {string} store - The name of the store to retrieve the item from.
 * @param {number} index - The index of the item to retrieve.
 * @returns {Promise<any>} - A promise that resolves to the item retrieved from the store.
 * @throws {Error} - Throws an error if the store or index is not provided.
 */
export async function get(store: string, index: number | null | undefined): Promise<Result<any, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: get("${store}", ${index}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    if (store && index != null) {
        const table = getTable(store);
        if (table.isErr()) return Err(new Error(`⟴ IDB: get("${store}", ${index}) failed — could not access table. ${table.unwrapErr().message}`));

        const item = await table.unwrap().get(index);
        return Ok(item ? restoreDocumentFile(store, item) : item);
    }
    else {
        return Err(new Error(`⟴ IDB: get() failed — missing required parameters (store=${JSON.stringify(store)}, index=${JSON.stringify(index)})`));
    }
}

/**
 * Add an item to the database.  Note that newly created items that have not been saved to the database yet
 * (status = 'C' and fresh = true) will NOT update their addedOn.  Since we haven't saved these to the database
 * yet, we should just replace the item in the database.
 *
 * @param {string} store
 * @param {object|[]} update
 * @returns {Promise<void>}
 * @throws {DexieError}
 */
export async function add(store: string, update: EntityType | EntityType[]): Promise<Result<void, Error>> {
    const owner = [getSessionFirmId(), getSessionUserId()];
    const items = Array.isArray(update) ? update : [update];
    const keys = items.map(item => JSON.stringify(['queue', ...owner, store, item?.id]));
    return persistenceGate.write(keys,
        () => addDurably(store, update), result => result.isOk());
}

/**
 * OWA-1083: the queues whose row this tab restamped (database), with the
 * stamp the row was dispatched with (`from`, the lowest when restamped more than once) and the
 * stamp it took (`to`). The restamp jumps this tab's clock past every stamp it has given, so a
 * later save stamped in between was dispatched after that row: it takes a
 * stamp after `to`, keeping the queue (run by addedOn) in dispatch order. Saves stamped before
 * `from` were dispatched earlier and keep their place.
 */
const restampedQueues = new Map<string, {from: number, to: number}>();

async function addDurably(store: string, update: EntityType | EntityType[]): Promise<Result<void, Error>> {
    const publishCount = captureCountPublisher();
    try {
        const dbResult = getDB();
        if (dbResult.isErr()) return Err(new Error(`⟴ IDB: add("${store}") failed — no database instance. ${dbResult.unwrapErr().message}`));

        const db = dbResult.unwrap();

        // Throw an error if we get called without a store or an item
        if (!store) return Err(new Error(`⟴ IDB: add() failed — called without a store name`));
        if (!update) return Err(new Error(`⟴ IDB: add("${store}") failed — no update payload passed`));

        // OWA-786 (CXR-5-004): a multi-item enqueue is one user action, so it
        // commits as ONE transaction. The old shape opened a transaction per
        // item and validated each item only when its turn came, so a malformed
        // later item (or a crash/quota error mid-batch) left a committed prefix
        // in the queue: hidden rows the next poll sent to the server while the
        // saga reported failure. Validate the whole batch first, then put every
        // row inside a single transaction that either all commits or all rolls
        // back.
        const items: EntityType[] = [];
        for (let raw of (Array.isArray(update) ? update : [update])) {
            let item = raw;

            if (logging) console.debug(`⟴ IDB: add(${store})`, item.addedOn, item.id);

            // If this is an old-style _addedOn, we need to cast it before adding it.
            if (Object.hasOwn(item, '_addedOn')) {
                item = cast(store, item);
            }

            // We cannot set an item without addedOn, as that is the key for this store
            if (!item.addedOn) return Err(new Error(`⟴ IDB: add("${store}") failed — item has no addedOn (id=${item.id}, status=${item.status}, fresh=${item.fresh})`));

            items.push(item);
        }

        // transaction() has already validated `store`, so getTable() can't fail inside it.
        const writeItems = async (rows: EntityType[]): Promise<number> => {
            let removedDuplicates = 0;
            // Enqueues in different stores share dispatch order too. Serialize their transactions
            // so a dependent row sees its parent's restamp before choosing its queue key.
            await db.transaction('rw!', db.tables, async () => {
                for (let item of rows) {
                    // A failed document can hold the only copy of a note or
                    // File; clean fresh creates are full snapshots.
                    // OWA-998: never for tasks. Each task create or edit is its own
                    // operation; a later one does not prove it contains an earlier one.
                    if (store !== 'task' && item.fresh && item.status === 'C') {
                        const t = db.table(store);
                        const matches = t.filter(o => (o.id === item.id) && o.fresh && (o.status === 'C') &&
                            (store !== 'document' || !o.fatalError));
                        const count = await matches.count();
                        if (count > 0) {
                            if (logging) console.debug(`⟴ IDB: add("${store}") deleting ${count} duplicate fresh/C entries for id=${item.id}`);
                            await matches.delete();
                            removedDuplicates += count;
                        }
                    }

                    // OWA-998, OWA-1083: the queue is keyed by addedOn alone, and two tabs can
                    // stamp the same one (both stamp after one held row). A different operation
                    // never replaces another's row, in any store: it takes a new addedOn (which
                    // the caller's object, and so the store row, keeps).
                    const opId = (item as {opId?: string}).opId;
                    const dispatchedAddedOn = item.addedOn;
                    // The first request waits for this transaction's locks. Reading the tab's
                    // restamp interval before it would race an earlier store's enqueue.
                    let holder = await db.table(store).get(item.addedOn);
                    // A restamp moves a row past this tab's later saves that were
                    // stamped before it and not yet queued: each of those follows it again.
                    const moved = restampedQueues.get(db.name);
                    if (moved && item.addedOn > moved.from && item.addedOn < moved.to) {
                        item.addedOn = newHRTimestamp(moved.to);
                        holder = await db.table(store).get(item.addedOn);
                    }
                    while (holder && holder.opId !== opId) {
                        item.addedOn = newHRTimestamp(item.addedOn);
                        holder = await db.table(store).get(item.addedOn);
                    }
                    if (item.addedOn !== dispatchedAddedOn) {
                        restampedQueues.set(db.name, {from: Math.min(moved?.from ?? dispatchedAddedOn, dispatchedAddedOn), to: item.addedOn});
                    }

                    // OWA-786 (CXR-5-005): preserve retry bookkeeping across a
                    // same-key replace unless the incoming row sets its own.
                    if (item.retryAttempts == null && item.nextRetryAt == null) {
                        const existing = await db.table(store).get(item.addedOn);
                        if (existing && (existing.retryAttempts != null || existing.nextRetryAt != null || existing.supersededByAddedOn != null)) {
                            item = {...item, retryAttempts: existing.retryAttempts, nextRetryAt: existing.nextRetryAt,
                                supersededByAddedOn: existing.supersededByAddedOn};
                        }
                    }

                    // put() adds or replaces the row with this addedOn key. An operation id the
                    // caller kept off its object's enumerable fields is written with the row.
                    if (logging) console.debug(`⟴ IDB: add("${store}") putting item id=${item.id}, addedOn=${item.addedOn}`);
                    const row = opId !== undefined && !Object.prototype.propertyIsEnumerable.call(item, 'opId')
                        ? Object.assign(Object.create(Object.getPrototypeOf(item)), item, {opId})
                        : item;
                    await db.table(store).put(row, item.addedOn);
                }
            });
            return removedDuplicates;
        };
        let removedDuplicates: number;
        try {
            // Native Blob/File storage keeps large retained payloads disk-backed.
            removedDuplicates = await writeItems(items);
        } catch (error) {
            if (store !== 'document' || !isDataCloneError(error) ||
                !items.some(item => (item as EntityType & {fileHandle?: unknown}).fileHandle instanceof Blob)) throw error;
            // Some WebKit IndexedDB implementations reject File cloning. Retry
            // the rolled-back transaction with bytes only for that case.
            removedDuplicates = await writeItems(await Promise.all(items.map(item => prepareDocumentFile(item))));
        }
        const count = Array.isArray(update) ? update.length : 1;
        publishCount(current => Math.max(0, current + count - removedDuplicates));
    }
    catch (err) {
        const itemCount = Array.isArray(update) ? update.length : 1;
        return Err(new Error(`⟴ IDB: add("${store}", ${itemCount} item(s)) failed. ${errMsg(err)}`));
    }

    return Ok(null);
}

// An acknowledged replacement can retire an older upload attempt only when
// it sent replacement contents. Preserve the older row for user recovery;
// a rename or other metadata-only acknowledgement cannot replace its bytes.
function acknowledgedReplacedDocumentContents(older: any, acknowledged: any): boolean {
    const olderHasFile = older.fileHandle instanceof Blob || isQueuedFileBytes(older.fileHandle);
    const olderHasContents = olderHasFile || older.plainText != null || older.buffer != null;
    if (!olderHasContents) return true;
    // A metadata-only rename may carry search text without uploading the older file.
    if (isRenameOnlyUpload(acknowledged)) return false;
    const newerHasFile = acknowledged.fileHandle instanceof Blob || isQueuedFileBytes(acknowledged.fileHandle);
    if (olderHasFile) return newerHasFile;
    const newerHasContents = newerHasFile || acknowledged.plainText != null || acknowledged.buffer != null;
    return newerHasContents;
}

/** Preserve older document operations after an acknowledged content replacement. */
export async function supersedeOlderDocumentVersions(acknowledged: EntityType): Promise<Result<number, Error>> {
    const publishCount = captureCountPublisher();
    const documentId = acknowledged.id;
    const acknowledgedAddedOn = acknowledged.addedOn;
    if (!documentId || !Number.isFinite(acknowledgedAddedOn)) return Err(new Error('Document acknowledgement lacks an identity or timestamp.'));
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(dbResult.unwrapErr());
    const db = dbResult.unwrap();
    try {
        let changed = 0;
        await db.transaction('rw!', 'document', async () => {
            const older = await db.table('document').where('documentId').equals(documentId)
                .filter(item => item.addedOn < acknowledgedAddedOn && !item.supersededByAddedOn &&
                    acknowledgedReplacedDocumentContents(item, acknowledged)).toArray();
            for (const item of older) {
                await db.table('document').update(item.addedOn, {supersededByAddedOn: acknowledgedAddedOn});
                changed++;
            }
        });
        if (changed) publishCount(current => Math.max(0, current - changed));
        return Ok(changed);
    } catch (err) {
        return Err(new Error(`Could not retain earlier versions of document ${documentId}: ${errMsg(err)}`));
    }
}

/**
 * Remove an item from a specific store by its key.
 *
 * @param {string} store - The name of the store from which to remove the item.
 * @param {IndexableType} key - The key of the item to be removed.
 * @returns {Promise<void>} - A promise that resolves when the item is removed.
 * @throws {Error} - Throws an error if the item with the specified key is not found in the store.
 */
export async function remove(store: string, key: IndexableType | null | undefined): Promise<Result<void, Error>> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: remove("${store}", key=${key}) failed — no database instance. ${dbResult.unwrapErr().message}`));
    // OWA-554: a null/undefined key can't index any row — nothing to remove. Return
    // a no-op success (callers pass an entity's nullable addedOn; an unpersisted
    // entity has no cached row keyed by addedOn).
    if (key == null) return Ok(undefined);
    if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key})`);

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: remove("${store}", key=${key}) failed — could not access table. ${table.unwrapErr().message}`));

    const t = table.unwrap();
    const item = await t.get(key);
    if (!item) {
        // Key not found - try to find and remove by matching the key as a different type (string vs number).
        // This handles stale IDB entries where the key type may have changed between versions.
        const altKey = typeof key === 'number' ? String(key) : Number(key);
        const altItem = isNaN(altKey as number) ? null : await t.get(altKey);
        if (altItem) {
            if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key}) found via alternate key type ${typeof altKey}(${altKey})`);
            await t.delete(altKey);
            if (store !== 'document' || !altItem.supersededByAddedOn) {
                publishCount(current => Math.max(0, current - 1));
            }
            return Ok(null);
        }

        console.warn(`⟴ IDB: remove("${store}", key=${key}) — key not found (tried original type ${typeof key} and alternate)`);
        return Ok(null);
    }

    if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key}) — deleting`);
    await t.delete(key);

    if (store !== 'document' || !item.supersededByAddedOn) {
        publishCount(current => Math.max(0, current - 1));
    }
    return Ok(null);
}

/**
 * OWA-998: retire an acknowledged task save and rebase every queued save that continues it, in
 * one transaction of the queue database (plan step 9). The rebased saves name the accepted version
 * as their base and drop their parent claim. The caller reports the success only after this
 * commits; when it fails, the row stays and is sent again, and its retry is recognized as
 * already committed.
 *
 * @param addedOn - the queue key of the acknowledged save
 * @param opId - its operation id; saves claiming it as their parent are rebased
 * @param version - the accepted version the server answered with
 */
export async function acknowledgeTask(addedOn: number, opId: string | null | undefined, version: VersionRef): Promise<Result<void, Error>> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: acknowledgeTask(${addedOn}) failed — no database instance. ${dbResult.unwrapErr().message}`));
    const db = dbResult.unwrap();
    try {
        let retired = 0;
        await db.transaction('rw!', 'task', async () => {
            const t = db.table('task');
            for (const key of [addedOn, String(addedOn)]) {
                const row = await t.get(key);
                if (row) {
                    await t.delete(key);
                    retired++;
                    // A merge of a held group retires exactly that group once it is accepted
                    for (const resolved of (Array.isArray(row.resolves) ? row.resolves : [])) {
                        if (await t.get(resolved)) {
                            await t.delete(resolved);
                            retired++;
                        }
                    }
                    break;
                }
            }
            if (opId) await t.filter(row => row.parentOp === opId).modify({baseVersion: version, parentOp: null});
        });
        if (retired) publishCount(current => Math.max(0, current - retired));
        return Ok(undefined);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: acknowledgeTask(${addedOn}) failed. ${errMsg(err)}`));
    }
}

/**
 * OWA-998: hold a task save the server refused as stale (plan step 14). The row stays in the
 * queue, is no longer sent, and carries the conflict the user resolves. One write; when it fails
 * the row is unchanged, is sent again, refused again, and held then.
 *
 * @returns the number of rows held (0 when the row is gone)
 */
export async function holdTask(addedOn: number, conflict: StaleConflict): Promise<Result<number, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: holdTask(${addedOn}) failed — no database instance. ${dbResult.unwrapErr().message}`));
    try {
        const t = dbResult.unwrap().table('task');
        // A queue row may be keyed by the number or by its string form, as acknowledgeTask allows
        let held = await t.update(addedOn, {conflict});
        if (!held) held = await t.update(String(addedOn), {conflict});
        if (!held) console.warn(`⟴ IDB: holdTask(${addedOn}) found no queue row to hold; the refused save is not held`);
        return Ok(held);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: holdTask(${addedOn}) failed. ${errMsg(err)}`));
    }
}

/**
 * OWA-998: retire exactly these task saves (by queue key) in one transaction: a held group the
 * user abandoned or merged, a refused reconciling save, or a refused save that changed nothing.
 */
export async function retireTasks(addedOns: number[]): Promise<Result<number, Error>> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: retireTasks failed — no database instance. ${dbResult.unwrapErr().message}`));
    const db = dbResult.unwrap();
    try {
        let retired = 0;
        await db.transaction('rw!', 'task', async () => {
            const t = db.table('task');
            for (const addedOn of addedOns) {
                for (const key of [addedOn, String(addedOn)]) {
                    if (await t.get(key)) {
                        await t.delete(key);
                        retired++;
                        break;
                    }
                }
            }
        });
        if (retired) publishCount(current => Math.max(0, current - retired));
        return Ok(retired);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: retireTasks failed. ${errMsg(err)}`));
    }
}

/**
 * Remove all IDB entries in a store matching an entity ID.
 * This is a fallback for when the primary key (addedOn) doesn't match due to key rewrites
 * in the Base constructor.
 *
 * @param {string} store - The name of the store (entity type)
 * @param {string} entityId - The entity ID to match against the store's `{store}Id` field
 * @returns {Promise<Result<number, Error>>} - The number of entries deleted
 */
export async function removeByEntityId(store: string, entityId: string): Promise<Result<number, Error>> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: removeByEntityId("${store}", entityId=${entityId}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: removeByEntityId("${store}", entityId=${entityId}) failed — could not access table. ${table.unwrapErr().message}`));

    const idKey = `${store}Id`;
    const count = await table.unwrap().where(idKey).equals(entityId).delete();
    if (count > 0) {
        if (logging) console.debug(`⟴ IDB: removeByEntityId(${store}, ${entityId}) removed ${count} entries`);
        publishCount(current => Math.max(0, current - count));
    }
    return Ok(count);
}

/**
 * Count all items across all IDB queue tables and set the queue count accordingly.
 * Call this after IDB is opened to ensure the count reflects any pre-existing entries.
 */
export async function initQueueCount(): Promise<void> {
    const publishCount = captureCountPublisher();
    const dbResult = getDB();
    if (dbResult.isErr()) return;

    let total = 0;
    for (const table of dbResult.unwrap().tables) {
        try {
            total += table.name === 'document'
                ? await table.filter(item => !item.supersededByAddedOn).count()
                : await table.count();
        }
        catch {
            // Table may not exist yet — skip it
        }
    }

    if (logging) console.debug(`⟴ IDB: initQueueCount() = ${total}`);
    publishCount(() => total);
}

/**
 *
 * The Ok value is the number of rows the update affected (OWA-786,
 * CXR-10-001): a fulfilled update that touched ZERO rows means the key no
 * longer exists, and callers that need durable proof (the retry bookkeeping
 * in processQueue) must treat anything other than exactly one as not proven.
 *
 * @param {string} store - which store to use: this will be the entityType, e.g. 'user', 'task', 'contact'
 * @param {number} key - object's key, generally the addedOn, but could also be the id
 * @param {any} updates - an object with properties to updates, e.g. {firmId: '12343-...', fresh: false}
 * @returns {Promise<Result<number, Error>>} - the affected-row count
 */
export async function update(store: string, key: number, updates: any): Promise<Result<number, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: update("${store}", key=${key}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: update("${store}", key=${key}) failed — could not access table. ${table.unwrapErr().message}`));

    const affected = await table.unwrap().update(key || updates.id, updates);

    return Ok(affected);
}
